
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export const NotificationSystem = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [emailData, setEmailData] = useState({
    to: '',
    subject: 'Test Email from ESG Platform',
    message: 'This is a test email to verify the notification system is working correctly.'
  });
  const { toast } = useToast();

  const sendTestEmail = async () => {
    if (!emailData.to || !emailData.subject || !emailData.message) {
      toast({
        title: "Validation Error",
        description: "Please fill in all fields before sending the test email.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    setTestResult(null);

    try {
      console.log('Sending test email:', emailData);

      // Get the current session for authorization
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No active session found');
      }

      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          to: emailData.to,
          subject: emailData.subject,
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #2563eb;">ESG Platform Test Email</h2>
              <p>${emailData.message.replace(/\n/g, '<br>')}</p>
              <hr style="margin: 20px 0; border: none; border-top: 1px solid #e5e7eb;">
              <p style="color: #666; font-size: 14px;">
                This email was sent from the ESG Platform notification system at ${new Date().toLocaleString()}.
              </p>
            </div>
          `,
          type: 'test'
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('Function invocation error:', error);
        throw error;
      }

      console.log('Email function response:', data);

      if (data?.success) {
        setTestResult({
          success: true,
          message: `Test email sent successfully to ${emailData.to}`
        });
        toast({
          title: "Email Sent Successfully",
          description: `Test email has been sent to ${emailData.to}`,
        });
      } else {
        throw new Error(data?.error || 'Unknown error occurred');
      }

    } catch (error) {
      console.error('Failed to send test email:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to send email';
      
      setTestResult({
        success: false,
        message: `Failed to send email: ${errorMessage}`
      });
      
      toast({
        title: "Email Send Failed",
        description: `Error: ${errorMessage}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Notification System
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            <div>
              <Label htmlFor="test-email">Recipient Email</Label>
              <Input
                id="test-email"
                type="email"
                value={emailData.to}
                onChange={(e) => setEmailData(prev => ({ ...prev, to: e.target.value }))}
                placeholder="Enter email address to test"
              />
            </div>

            <div>
              <Label htmlFor="test-subject">Email Subject</Label>
              <Input
                id="test-subject"
                value={emailData.subject}
                onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Email subject"
              />
            </div>

            <div>
              <Label htmlFor="test-message">Message</Label>
              <Textarea
                id="test-message"
                value={emailData.message}
                onChange={(e) => setEmailData(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Email message content"
                rows={4}
              />
            </div>

            <Button 
              onClick={sendTestEmail} 
              disabled={isLoading}
              className="flex items-center gap-2"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {isLoading ? 'Sending...' : 'Send Test Email'}
            </Button>
          </div>

          {testResult && (
            <Alert className={testResult.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
              {testResult.success ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4 text-red-600" />
              )}
              <AlertDescription className={testResult.success ? 'text-green-800' : 'text-red-800'}>
                {testResult.message}
              </AlertDescription>
            </Alert>
          )}

          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium mb-2">System Status</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Email Service:</span>
                <span className="text-green-600 font-medium">Active</span>
              </div>
              <div className="flex justify-between">
                <span>Last Test:</span>
                <span className="text-gray-600">
                  {testResult ? new Date().toLocaleString() : 'Not tested yet'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Function Status:</span>
                <span className="text-blue-600 font-medium">Ready</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
